import { z } from 'zod';
import {
  compareQty,
  isPositive,
  maxQty,
  multiplyQty,
  qtyToString,
  subQty,
} from '../quantity/index.js';

/**
 * Purchase requests and orders: statuses and how receiving moves an order along.
 *
 * A **purchase request** asks for stock from one supplier; a manager judges it product by
 * product in the approvals inbox (approval type `purchase_request`, permission
 * `purchasing.approve`); approving makes the **purchase order** from the approved lines.
 * "Sent" is a status a person sets (printed, e-mailed by hand): V1 writes nothing to any
 * external system. Receiving derives the rest: `partially_received` → `received`.
 * @module
 */

/** A purchase request's lifecycle. */
export const PURCHASE_REQUEST_STATUSES = [
  'requested',
  'approved',
  'rejected',
  'cancelled',
] as const;
/** One of {@link PURCHASE_REQUEST_STATUSES}. */
export type PurchaseRequestStatus = (typeof PURCHASE_REQUEST_STATUSES)[number];

/** Where a purchase request came from. */
export const PURCHASE_REQUEST_ORIGINS = ['admin', 'station', 'suggestion'] as const;
/** One of {@link PURCHASE_REQUEST_ORIGINS}. */
export type PurchaseRequestOrigin = (typeof PURCHASE_REQUEST_ORIGINS)[number];

/** A purchase order's lifecycle (see the module note). */
export const PO_STATUSES = [
  'open',
  'sent',
  'partially_received',
  'received',
  'closed',
  'cancelled',
] as const;
/** One of {@link PO_STATUSES}. */
export type PoStatus = (typeof PO_STATUSES)[number];
/** zod schema for {@link PoStatus}. */
export const poStatusSchema = z.enum(PO_STATUSES);

/** Statuses in which an order still expects deliveries (stations list these). */
export const PO_OPEN_STATUSES = [
  'open',
  'sent',
  'partially_received',
] as const satisfies readonly PoStatus[];

/** True when the order still expects deliveries. */
export function isPoOpen(status: PoStatus): boolean {
  return (PO_OPEN_STATUSES as readonly string[]).includes(status);
}

/** One order line's quantities, in the product's base unit. */
export interface PoLineProgress {
  orderedBaseQty: string;
  receivedBaseQty: string;
}

/** How much of the order has arrived. */
export type ReceivingProgress = 'none' | 'partial' | 'full';

/** What is still to come on a line: `max(0, ordered − received)`. */
export function outstandingBaseQty(line: PoLineProgress): string {
  return qtyToString(maxQty(0, subQty(line.orderedBaseQty, line.receivedBaseQty)));
}

/**
 * `none`: nothing received on any line; `full`: every line received at least what was
 * ordered; otherwise `partial`. An order without lines has received nothing.
 */
export function receivingProgress(lines: readonly PoLineProgress[]): ReceivingProgress {
  if (lines.length === 0 || lines.every((l) => !isPositive(l.receivedBaseQty))) return 'none';
  return lines.every((l) => compareQty(l.receivedBaseQty, l.orderedBaseQty) >= 0)
    ? 'full'
    : 'partial';
}

/**
 * The order's status once its lines hold `lines` (after a receipt): a closed or cancelled
 * order stays so (goods that arrive anyway are flagged on the receipt, never refused);
 * otherwise `received` when every line is in, `partially_received` when some of it is,
 * and unchanged (`open` / `sent`) when nothing is.
 */
export function poStatusAfterReceipt(
  current: PoStatus,
  lines: readonly PoLineProgress[],
): PoStatus {
  if (current === 'closed' || current === 'cancelled') return current;
  const progress = receivingProgress(lines);
  if (progress === 'full') return 'received';
  if (progress === 'partial') return 'partially_received';
  return current === 'sent' ? 'sent' : 'open';
}

/** What a person may still do with an order. */
export interface PoActions {
  /** Change quantities, prices and lines: nothing received yet, not cancelled or closed. */
  editLines: boolean;
  /** Change the expected date and the note. */
  editDetails: boolean;
  markSent: boolean;
  /** Cancel: only before anything was received. */
  cancel: boolean;
  /** Close short: stop expecting the rest (after a partial delivery, or any time open). */
  close: boolean;
}

/** The actions {@link PoActions} allows for an order in `status`, given what has arrived. */
export function poActions(status: PoStatus, anythingReceived: boolean): PoActions {
  const live = isPoOpen(status);
  return {
    editLines: live && !anythingReceived,
    editDetails: live,
    markSent: status === 'open',
    cancel: live && !anythingReceived,
    close: live,
  };
}

/** Base quantity of `packs` packs of `baseQtyPerPack` each (exact). */
export function packsToBase(packs: string, baseQtyPerPack: string): string {
  return qtyToString(multiplyQty(packs, baseQtyPerPack));
}
